import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { Table } from '@cureskin/api-client';
import { EventLoggerService } from '@services/event-logger-service';
import { TimeService } from '@services/time-service';
import { AnimationOptions } from 'ngx-lottie';
import { AppConfig } from '../app.config';
import { resetCacheByName } from '@services/connection-service/cache-interceptor';
import {QuizNextQuestionResponse} from '@cureskin/api-client/src/server-api/api';

@Component({
  selector: 'quiz-game',
  templateUrl: './quiz-game.html',
  standalone: false,
})
export class QuizGameComponent implements OnInit {
  maxRewardAmount: number = 3;
  currentQuestion: QuizNextQuestionResponse['data']['question'];
  selectedOption: any = null;
  lastTwoQuestions: any[] = [];
  alreadyAnswered: boolean = false;
  noMoreQuestions: boolean = false;
  rewardMoney: number;
  showConfettiAnimation: boolean = false;
  loading: boolean = true;
  isInternalUser: boolean = false;
  correctAnswer: string;
  isCorrectAnswer: boolean;
  isWrongAnswer: boolean;
  wrongAnswerDescription: string;
  userAnswers: any[] = [];
  options: AnimationOptions = {
    path: '/assets/animation.json',
  };
  styles: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    bottom: '0',
  };
  quizExp: any;
  user: any;

  sequenceCounter: number = 0;
  isUsersLastOrderIsWithin30Days: boolean = false;


  constructor(
    private conn: ConnectionService,
    public appConfig: AppConfig,
    private timeService: TimeService,
    private eventLogger: EventLoggerService,
  ) { }

  async ngOnInit(): Promise<any> {
    this.user = this.conn.getActingUser();
    this.isInternalUser = this.conn.isInternalUser();
    const experiments = await this.conn.findUserActiveExperiments();
    experiments.forEach(async (experiment: any): Promise<any> => {
      if (experiment.key === 'game_new') {
        this.maxRewardAmount = isNaN(+experiment.variant.maxRewardAmount) ? this.appConfig.Shared.QUIZ_MAX_REWARD : +experiment.variant.maxRewardAmount;
      }
    });
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'quiz-multi-question' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.isLastOrderWithin30Days();
    this.startGame();
  }

  async startGame(): Promise<void> {
    this.currentQuestion = null;
    this.isCorrectAnswer = false;
    this.isWrongAnswer = false;
    this.selectedOption = false;
    this.loading = true;

    const data = await this.conn.getNextQuizQuestion();
    switch (data.status) {
      case "ALL_ANSWERED": {
        this.noMoreQuestions = true;
        break;
      }
      case "DAILY_LIMIT_REACHED": {
        this.alreadyAnswered = true;
        break;
      }
      default: {
        this.currentQuestion = data.question;
      }
    }

    this.loading = false;
  }

  back(): void {
    this.conn.navigateToURL('/user?tab=home', false, true);
  }

  async submit(option: any): Promise<any> {
    if (this.isInternalUser || this.selectedOption) {
      return;
    }
    this.selectedOption = option;
    const answerIndex = this.currentQuestion.options.indexOf(option);
    const response = await this.conn.storeQuizQuestionAnswer(this.currentQuestion.objectId, answerIndex);
    this.isCorrectAnswer = response.correct;
    this.isWrongAnswer = !response.correct;
    this.wrongAnswerDescription = response.description;
    this.correctAnswer = this.currentQuestion.options[response.correctAnswerIndex];

    if (this.isCorrectAnswer) {
      if (this.isUsersLastOrderIsWithin30Days) {
        const claim = await this.giveReward();
        await this.conn.claimReward(claim.id);
      }

      const eventName = this.isUsersLastOrderIsWithin30Days
        ? 'quiz-correct-answer'
        : 'quiz-correct-answer-no-reward';
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ pageName: eventName }));

      const timeout = setTimeout((): void => {
        this.showConfettiAnimation = true;
        clearTimeout(timeout);
      }, 1500);

    } else if (this.isWrongAnswer) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ pageName: 'quiz-wrong-answer' }));
    }
  }

  giveReward(): any {
    // reward between 1 to 15 rs
    this.rewardMoney = Math.floor(Math.random() * (this.maxRewardAmount) + 1);
    const reward = new Table.Reward();
    reward.set('type', 'GAME_REWARD');
    reward.set('isClaimed', false);
    reward.set('amount', this.rewardMoney);
    reward.set('user', this.conn.getActingUser());
    reward.set('isReferrer', false);
    reward.set('source', 'Game Reward');
    resetCacheByName('fetchCashBalance');
    return reward.save();
  }

  isLastOrderWithin30Days(): void {
    const lastOrderDate = this.conn.getActingUser().get('lastOrderDeliveredDate');

    // First check if we have a valid date
    if (!lastOrderDate) {
      this.isUsersLastOrderIsWithin30Days = false;
    }

    // Convert to Date object if it's not already
    const lastOrderDeliveryDate = new Date(lastOrderDate);
    const today = new Date();

    // Get the difference in days
    const daysDifference = this.timeService.getDaysDifference(lastOrderDeliveryDate, today);

    // Check if it's within 30 days
    this.isUsersLastOrderIsWithin30Days = daysDifference <= 30;
  }

}
